/**
 * Arcℏimedian DSaaS — API Services
 * Typed service functions for each backend domain.
 */

import api, { uploadFile } from './client';
import type * as T from './types';

// ═══════════════════════════════════════════════
// AUTH SERVICE
// ═══════════════════════════════════════════════

export const authService = {
  register: (data: T.RegisterRequest) =>
    api.post<T.TokenResponse>('/auth/register', data),

  login: (data: T.LoginRequest) =>
    api.post<T.TokenResponse>('/auth/login', data),

  refresh: (refreshToken: string) =>
    api.post<T.TokenResponse>('/auth/refresh', { refresh_token: refreshToken }),

  me: () =>
    api.get<T.User>('/auth/me'),

  updateProfile: (data: T.UserUpdate) =>
    api.patch<T.User>('/auth/me', data),

  updatePreferences: (prefs: Record<string, unknown>) =>
    api.patch<T.User>('/auth/me', { preferences: prefs }),

  changePassword: (data: T.ChangePasswordRequest) =>
    api.put('/auth/me/password', data),

  verifyEmail: (token: string) =>
    api.post<T.User>('/auth/verify-email', { token }),

  resendVerification: () =>
    api.post('/auth/resend-verification'),
};

// ═══════════════════════════════════════════════
// USERS SERVICE
// ═══════════════════════════════════════════════

export const usersService = {
  list: (params?: { page?: number; page_size?: number; search?: string; role?: string; status?: string }) =>
    api.get<{ users: T.User[]; total: number; page: number; page_size: number }>('/users', { params }),

  get: (id: string) =>
    api.get<T.User>(`/users/${id}`),

  update: (id: string, data: Partial<T.UserUpdate>) =>
    api.patch<T.User>(`/users/${id}`, data),

  updateRole: (id: string, role: string) =>
    api.put(`/users/${id}/role`, { role }),

  deactivate: (id: string) =>
    api.post(`/users/${id}/deactivate`),

  reactivate: (id: string) =>
    api.post(`/users/${id}/reactivate`),

  delete: (id: string) =>
    api.delete(`/users/${id}`),

  stats: () =>
    api.get('/users/stats'),
};

// ═══════════════════════════════════════════════
// WORKSPACES SERVICE
// ═══════════════════════════════════════════════

export const workspacesService = {
  create: (data: T.WorkspaceCreate) =>
    api.post<T.Workspace>('/workspaces', data),

  list: (params?: { page?: number; page_size?: number; search?: string; visibility?: string }) =>
    api.get<T.WorkspaceListResponse>('/workspaces', { params }),

  get: (id: string) =>
    api.get<T.WorkspaceDetail>(`/workspaces/${id}`),

  update: (id: string, data: Partial<T.WorkspaceCreate>) =>
    api.patch<T.Workspace>(`/workspaces/${id}`, data),

  delete: (id: string) =>
    api.delete(`/workspaces/${id}`),

  transfer: (id: string, newOwnerId: string) =>
    api.post<T.Workspace>(`/workspaces/${id}/transfer`, null, { params: { new_owner_id: newOwnerId } }),

  stats: () =>
    api.get('/workspaces/stats'),

  roles: () =>
    api.get('/workspaces/roles'),

  // Members
  members: {
    list: (workspaceId: string) =>
      api.get<{ members: T.MemberDetail[]; total: number }>(`/workspaces/${workspaceId}/members`),

    add: (workspaceId: string, data: { user_id: string; role: string }) =>
      api.post<T.Member>(`/workspaces/${workspaceId}/members`, data),

    update: (workspaceId: string, userId: string, data: { role?: string; can_write?: boolean; can_delete?: boolean; can_admin?: boolean; can_deploy?: boolean }) =>
      api.patch<T.Member>(`/workspaces/${workspaceId}/members/${userId}`, data),

    remove: (workspaceId: string, userId: string) =>
      api.delete(`/workspaces/${workspaceId}/members/${userId}`),
  },

  // Permissions
  permissions: {
    mine: (workspaceId: string) =>
      api.get<T.Permissions>(`/workspaces/${workspaceId}/permissions`),

    forUser: (workspaceId: string, userId: string) =>
      api.get<T.Permissions>(`/workspaces/${workspaceId}/permissions/${userId}`),
  },

  // Invites
  invites: {
    create: (workspaceId: string, data: { email: string; role: string; message?: string }) =>
      api.post<T.Invite>(`/workspaces/${workspaceId}/invites`, data),

    bulk: (workspaceId: string, data: { emails: string[]; role: string; message?: string }) =>
      api.post(`/workspaces/${workspaceId}/invites/bulk`, data),

    list: (workspaceId: string, status?: string) =>
      api.get<{ invites: T.Invite[]; total: number }>(`/workspaces/${workspaceId}/invites`, { params: { status } }),

    accept: (token: string) =>
      api.post<T.Member>('/workspaces/invites/accept', { token }),

    decline: (token: string) =>
      api.post('/workspaces/invites/decline', { token }),

    revoke: (workspaceId: string, inviteId: string) =>
      api.post<T.Invite>(`/workspaces/${workspaceId}/invites/${inviteId}/revoke`),

    resend: (workspaceId: string, inviteId: string) =>
      api.post<T.Invite>(`/workspaces/${workspaceId}/invites/${inviteId}/resend`),
  },
};

// ═══════════════════════════════════════════════
// DATASETS SERVICE
// ═══════════════════════════════════════════════

export const datasetsService = {
  upload: (file: File, fields: { name: string; workspace_id: string; description?: string }, onProgress?: (p: number) => void) =>
    uploadFile('/datasets/upload', file, fields as Record<string, string>, onProgress),

  createSample: () =>
    api.post<T.Dataset>('/datasets/use-sample'),

  list: (params?: { page?: number; page_size?: number; workspace_id?: string; format?: string; status?: string; search?: string }) =>
    api.get<{ datasets: T.Dataset[]; total: number; page: number; page_size: number }>('/datasets', { params }),

  get: (id: string) =>
    api.get<T.Dataset>(`/datasets/${id}`),

  columns: (id: string) =>
    api.get<T.DatasetColumn[]>(`/datasets/${id}/columns`),

  preview: (id: string) =>
    api.get(`/datasets/${id}/preview`),

  versions: (id: string) =>
    api.get(`/datasets/${id}/versions`),

  update: (id: string, data: { name?: string; description?: string; tags?: string[] }) =>
    api.patch<T.Dataset>(`/datasets/${id}`, data),

  delete: (id: string, permanent?: boolean) =>
    api.delete(`/datasets/${id}`, { params: { permanent } }),

  stats: () =>
    api.get('/datasets/stats'),
};

// ═══════════════════════════════════════════════
// ML MODELS SERVICE
// ═══════════════════════════════════════════════

export const modelsService = {
  create: (data: { name: string; framework: string; task: string; workspace_id: string; algorithm?: string; target_column?: string; feature_names?: string[] }) =>
    api.post<T.MLModel>('/models', data),

  list: (params?: { page?: number; page_size?: number; workspace_id?: string; framework?: string; task?: string; status?: string }) =>
    api.get<{ models: T.MLModel[]; total: number; page: number; page_size: number }>('/models', { params }),

  get: (id: string) =>
    api.get<T.MLModel>(`/models/${id}`),

  update: (id: string, data: Partial<{ name: string; description: string; status: string }>) =>
    api.patch<T.MLModel>(`/models/${id}`, data),

  delete: (id: string) =>
    api.delete(`/models/${id}`),

  stats: () =>
    api.get('/models/stats'),

  // Versions
  versions: {
    create: (modelId: string, data: { version_tag?: string; hyperparameters?: Record<string, unknown>; metrics?: Record<string, number> }) =>
      api.post<T.ModelVersion>(`/models/${modelId}/versions`, data),

    list: (modelId: string) =>
      api.get<T.ModelVersion[]>(`/models/${modelId}/versions`),

    updateStage: (modelId: string, versionId: string, stage: string) =>
      api.patch<T.ModelVersion>(`/models/${modelId}/versions/${versionId}`, { stage }),

    compare: (modelId: string, versionIds: string[]) =>
      api.post(`/models/${modelId}/versions/compare`, { version_ids: versionIds }),
  },

  // Deployments
  deploy: (modelId: string, data: { version_id: string; environment: string; traffic_percentage?: number }) =>
    api.post<T.ModelDeployment>(`/models/${modelId}/deploy`, data),

  rollback: (modelId: string) =>
    api.post(`/models/${modelId}/rollback`),

  deployments: (modelId: string) =>
    api.get<T.ModelDeployment[]>(`/models/${modelId}/deployments`),

  // Predictions
  predict: (modelId: string, inputData: Record<string, unknown>) =>
    api.post<T.Prediction>(`/models/${modelId}/predict`, { input_data: inputData }),

  predictBatch: (modelId: string, inputs: Record<string, unknown>[]) =>
    api.post(`/models/${modelId}/predict/batch`, { inputs }),

  predictions: (modelId: string, params?: { page?: number; page_size?: number }) =>
    api.get(`/models/${modelId}/predictions`, { params }),
};

// ═══════════════════════════════════════════════
// EXPERIMENTS SERVICE
// ═══════════════════════════════════════════════

export const experimentsService = {
  create: (data: { name: string; workspace_id: string; description?: string; hypothesis?: string; primary_metric?: string; metric_goal?: string; tags?: string[] }) =>
    api.post<T.Experiment>('/experiments', data),

  list: (params?: { page?: number; page_size?: number; workspace_id?: string; status?: string; search?: string }) =>
    api.get<{ experiments: T.Experiment[]; total: number; page: number; page_size: number }>('/experiments', { params }),

  get: (id: string) =>
    api.get<T.Experiment & { runs: T.ExperimentRun[] }>(`/experiments/${id}`),

  update: (id: string, data: Partial<{ name: string; description: string; status: string; primary_metric: string; metric_goal: string }>) =>
    api.patch<T.Experiment>(`/experiments/${id}`, data),

  delete: (id: string) =>
    api.delete(`/experiments/${id}`),

  stats: (workspaceId?: string) =>
    api.get('/experiments/stats', { params: { workspace_id: workspaceId } }),

  metricKeys: (experimentId: string) =>
    api.get<string[]>(`/experiments/${experimentId}/metric-keys`),

  leaderboard: (experimentId: string, metricKey: string, goal?: string, topN?: number) =>
    api.get<{ entries: T.LeaderboardEntry[]; metric_key: string; goal: string }>(`/experiments/${experimentId}/leaderboard`, {
      params: { metric_key: metricKey, goal, top_n: topN },
    }),

  compare: (experimentId: string, runIds: string[]) =>
    api.post(`/experiments/${experimentId}/compare`, null, { params: { ids: runIds } }),

  // Runs
  runs: {
    create: (experimentId: string, data: { run_name?: string; params?: Record<string, unknown>; tags?: string[]; git_commit?: string; git_branch?: string }) =>
      api.post<T.ExperimentRun>(`/experiments/${experimentId}/runs`, data),

    list: (experimentId: string, params?: { page?: number; page_size?: number; status?: string }) =>
      api.get<{ runs: T.ExperimentRun[]; total: number }>(`/experiments/${experimentId}/runs`, { params }),

    get: (experimentId: string, runId: string) =>
      api.get<T.RunDetail>(`/experiments/${experimentId}/runs/${runId}`),

    update: (experimentId: string, runId: string, data: { status?: string; notes?: string; error_message?: string }) =>
      api.patch<T.ExperimentRun>(`/experiments/${experimentId}/runs/${runId}`, data),

    // Metrics
    logMetric: (experimentId: string, runId: string, data: { key: string; value: number; step?: number }) =>
      api.post<T.RunMetric>(`/experiments/${experimentId}/runs/${runId}/metrics`, data),

    logMetricsBatch: (experimentId: string, runId: string, metrics: { key: string; value: number; step?: number }[]) =>
      api.post(`/experiments/${experimentId}/runs/${runId}/metrics/batch`, { metrics }),

    metricHistory: (experimentId: string, runId: string, key: string) =>
      api.get<{ key: string; values: { value: number; step?: number; timestamp: string }[] }>(
        `/experiments/${experimentId}/runs/${runId}/metrics/${key}`
      ),

    // Params
    logParam: (experimentId: string, runId: string, data: { key: string; value: string; value_type?: string }) =>
      api.post<T.RunParam>(`/experiments/${experimentId}/runs/${runId}/params`, data),

    logParamsBatch: (experimentId: string, runId: string, params: { key: string; value: string }[]) =>
      api.post(`/experiments/${experimentId}/runs/${runId}/params/batch`, { params }),

    // Artifacts
    logArtifact: (experimentId: string, runId: string, data: { name: string; artifact_type: string; path: string; size_bytes?: number }) =>
      api.post<T.RunArtifact>(`/experiments/${experimentId}/runs/${runId}/artifacts`, data),

    listArtifacts: (experimentId: string, runId: string) =>
      api.get<T.RunArtifact[]>(`/experiments/${experimentId}/runs/${runId}/artifacts`),
  },
};

// ═══════════════════════════════════════════════
// PIPELINES SERVICE
// ═══════════════════════════════════════════════

export const pipelinesService = {
  create: (data: { name: string; workspace_id: string; description?: string; trigger_type?: string; steps?: unknown[] }) =>
    api.post<T.Pipeline>('/pipelines', data),

  list: (params?: { page?: number; page_size?: number; workspace_id?: string; status?: string; search?: string }) =>
    api.get<{ pipelines: T.Pipeline[]; total: number; page: number; page_size: number }>('/pipelines', { params }),

  get: (id: string) =>
    api.get<T.Pipeline>(`/pipelines/${id}`),

  update: (id: string, data: Partial<{ name: string; description: string; status: string }>) =>
    api.patch<T.Pipeline>(`/pipelines/${id}`, data),

  delete: (id: string) =>
    api.delete(`/pipelines/${id}`),

  stats: () =>
    api.get('/pipelines/stats'),

  // Steps
  steps: {
    add: (pipelineId: string, data: { name: string; step_type: string; execution_order: number; config?: Record<string, unknown> }) =>
      api.post<T.PipelineStep>(`/pipelines/${pipelineId}/steps`, data),

    list: (pipelineId: string) =>
      api.get<T.PipelineStep[]>(`/pipelines/${pipelineId}/steps`),

    update: (pipelineId: string, stepId: string, data: Partial<{ name: string; config: Record<string, unknown>; is_enabled: boolean }>) =>
      api.patch<T.PipelineStep>(`/pipelines/${pipelineId}/steps/${stepId}`, data),

    delete: (pipelineId: string, stepId: string) =>
      api.delete(`/pipelines/${pipelineId}/steps/${stepId}`),
  },

  // Runs
  runs: {
    trigger: (pipelineId: string) =>
      api.post<T.PipelineRun>(`/pipelines/${pipelineId}/run`),

    list: (pipelineId: string, params?: { page?: number; page_size?: number }) =>
      api.get<{ runs: T.PipelineRun[]; total: number }>(`/pipelines/${pipelineId}/runs`, { params }),

    get: (pipelineId: string, runId: string) =>
      api.get<T.PipelineRun>(`/pipelines/${pipelineId}/runs/${runId}`),

    cancel: (pipelineId: string, runId: string) =>
      api.post(`/pipelines/${pipelineId}/runs/${runId}/cancel`),

    retry: (pipelineId: string, runId: string) =>
      api.post<T.PipelineRun>(`/pipelines/${pipelineId}/runs/${runId}/retry`),
  },

  // Schedule
  schedule: {
    create: (pipelineId: string, data: { frequency: string; cron_expression?: string; timezone?: string }) =>
      api.post<T.ScheduledJob>(`/pipelines/${pipelineId}/schedule`, data),

    get: (pipelineId: string) =>
      api.get<T.ScheduledJob>(`/pipelines/${pipelineId}/schedule`),

    update: (pipelineId: string, data: Partial<{ frequency: string; cron_expression: string; is_active: boolean }>) =>
      api.patch<T.ScheduledJob>(`/pipelines/${pipelineId}/schedule`, data),

    delete: (pipelineId: string) =>
      api.delete(`/pipelines/${pipelineId}/schedule`),
  },
};

// ═══════════════════════════════════════════════
// BILLING SERVICE
// ═══════════════════════════════════════════════

export const billingService = {
  plans: {
    list: (includeInactive?: boolean) =>
      api.get<T.Plan[]>('/billing/plans', { params: { include_inactive: includeInactive } }),

    get: (id: string) =>
      api.get<T.Plan>(`/billing/plans/${id}`),

    seed: () =>
      api.post<T.Plan[]>('/billing/plans/seed'),
  },

  subscriptions: {
    create: (data: { workspace_id: string; plan_id: string; billing_interval?: string }) =>
      api.post<T.Subscription>('/billing/subscriptions', data),

    getForWorkspace: (workspaceId: string) =>
      api.get<T.SubscriptionDetail>(`/billing/subscriptions/workspace/${workspaceId}`),

    changePlan: (subscriptionId: string, data: { new_plan_id: string; billing_interval?: string }) =>
      api.post<T.Subscription>(`/billing/subscriptions/${subscriptionId}/change-plan`, data),

    cancel: (subscriptionId: string, params?: { immediate?: boolean; reason?: string }) =>
      api.post<T.Subscription>(`/billing/subscriptions/${subscriptionId}/cancel`, null, { params }),

    reactivate: (subscriptionId: string) =>
      api.post<T.Subscription>(`/billing/subscriptions/${subscriptionId}/reactivate`),
  },

  usage: {
    record: (workspaceId: string, data: { metric: string; quantity: number }) =>
      api.post('/billing/usage', data, { params: { workspace_id: workspaceId } }),

    dashboard: (workspaceId: string) =>
      api.get<T.UsageDashboard>(`/billing/usage/dashboard/${workspaceId}`),

    history: (workspaceId: string, params?: { metric?: string; days?: number }) =>
      api.get(`/billing/usage/history/${workspaceId}`, { params }),
  },

  invoices: {
    generate: (workspaceId: string) =>
      api.post(`/billing/invoices/generate/${workspaceId}`),

    list: (workspaceId: string, params?: { page?: number; page_size?: number; status?: string }) =>
      api.get<{ invoices: T.Invoice[]; total: number }>(`/billing/invoices/${workspaceId}`, { params }),

    get: (invoiceId: string) =>
      api.get<T.Invoice & { line_items: T.InvoiceLineItem[] }>(`/billing/invoices/detail/${invoiceId}`),

    pay: (invoiceId: string) =>
      api.post<T.Invoice>(`/billing/invoices/${invoiceId}/pay`),

    void: (invoiceId: string) =>
      api.post<T.Invoice>(`/billing/invoices/${invoiceId}/void`),
  },

  paymentMethods: {
    add: (workspaceId: string, data: { type: string; card_brand?: string; card_last_four?: string; card_exp_month?: number; card_exp_year?: number; is_default?: boolean }) =>
      api.post<T.PaymentMethod>(`/billing/payment-methods/${workspaceId}`, data),

    list: (workspaceId: string) =>
      api.get<T.PaymentMethod[]>(`/billing/payment-methods/${workspaceId}`),

    setDefault: (workspaceId: string, methodId: string) =>
      api.post<T.PaymentMethod>(`/billing/payment-methods/${workspaceId}/${methodId}/default`),

    delete: (workspaceId: string, methodId: string) =>
      api.delete(`/billing/payment-methods/${workspaceId}/${methodId}`),
  },

  stats: () =>
    api.get('/billing/stats'),

  checkout: {
    create: (workspaceId: string, data: { plan_id: string; interval: string }) =>
      api.post<{ url: string }>('/billing/checkout', data, { params: { workspace_id: workspaceId } }),

    portal: (workspaceId: string) =>
      api.post<{ url: string }>('/billing/portal', null, { params: { workspace_id: workspaceId } }),
  },
};

// ═══════════════════════════════════════════════
// NOTIFICATIONS SERVICE
// ═══════════════════════════════════════════════

export const notificationsService = {
  list: (params?: { page?: number; page_size?: number; unread_only?: boolean; type?: string; priority?: string }) =>
    api.get<{ notifications: T.Notification[]; total: number; unread_count: number; page: number; page_size: number }>(
      '/notifications', { params }
    ),

  counts: () =>
    api.get<T.NotificationCounts>('/notifications/count'),

  send: (data: { user_id: string; type: string; title: string; message: string; priority?: string; action_url?: string; workspace_id?: string }) =>
    api.post<T.Notification>('/notifications', data),

  broadcast: (data: { title: string; message: string; workspace_id?: string; user_ids?: string[] }) =>
    api.post('/notifications/broadcast', data),

  markRead: (ids: string[]) =>
    api.post('/notifications/read', { notification_ids: ids }),

  markAllRead: () =>
    api.post('/notifications/read-all'),

  delete: (id: string) =>
    api.delete(`/notifications/${id}`),

  clearRead: () =>
    api.delete('/notifications/clear/read'),

  preferences: {
    get: () =>
      api.get<T.NotificationPreference>('/notifications/preferences'),

    update: (data: Partial<T.NotificationPreference>) =>
      api.put<T.NotificationPreference>('/notifications/preferences', data),
  },
};

// ═══════════════════════════════════════════════
// AUDIT SERVICE
// ═══════════════════════════════════════════════

export const auditService = {
  list: (params?: {
    page?: number; page_size?: number; workspace_id?: string; actor_id?: string;
    action?: string; category?: string; resource_type?: string; date_from?: string;
    date_to?: string; search?: string; is_sensitive?: boolean;
  }) =>
    api.get<{ entries: T.AuditEntry[]; total: number; page: number; page_size: number }>('/audit', { params }),

  get: (id: string) =>
    api.get<T.AuditEntry>(`/audit/${id}`),

  resourceHistory: (resourceType: string, resourceId: string) =>
    api.get<T.AuditEntry[]>(`/audit/resource/${resourceType}/${resourceId}`),

  userActivity: (userId: string, days?: number) =>
    api.get<T.AuditEntry[]>(`/audit/user/${userId}`, { params: { days } }),

  stats: (workspaceId?: string) =>
    api.get('/audit/stats', { params: { workspace_id: workspaceId } }),

  activityFeed: (params?: { workspace_id?: string; user_id?: string; page?: number; page_size?: number }) =>
    api.get<{ items: T.ActivityFeedItem[]; total: number; page: number; page_size: number }>('/activity', { params }),
};

// ═══════════════════════════════════════════════
// ANALYSES SERVICE
// ═══════════════════════════════════════════════

export const analysesService = {
  list: (params?: { page?: number; page_size?: number; status?: string; dataset_id?: string }) =>
    api.get<T.AnalysisList>('/analyses', { params }),

  get: (id: string) =>
    api.get<T.AnalysisDetail>(`/analyses/${id}`),

  create: (data: T.AnalysisCreate) =>
    api.post<T.AnalysisDetail>('/analyses', data),

  progress: (id: string) =>
    api.get<T.AnalysisProgress>(`/analyses/${id}/progress`),

  update: (id: string, data: { name?: string; description?: string }) =>
    api.patch<T.AnalysisDetail>(`/analyses/${id}`, data),

  retry: (id: string) =>
    api.post<T.AnalysisDetail>(`/analyses/${id}/retry`),

  delete: (id: string) =>
    api.delete(`/analyses/${id}`),
};
